import Immutable from 'immutable';
import { createMigration } from './utils';

export const migrationTo9_204 = createMigration({
    migrateVideo(video) {
        return video.withMutations(map => {
            map.get('sceneGroups').forEach((sceneGroup, i) => {
                map.setIn(['sceneGroups', i], this.migrateSceneGroup(sceneGroup));
            });
            map.get('slides').forEach((scene, i) => {
                map.setIn(['slides', i], this.migrateScene(scene));
            });
        });
    },
    migrateSceneGroup(sceneGroup) {
        return sceneGroup.withMutations(map => {
            const path = ['mediaAsset'];
            migrateAsset(map, path);
        });
    },
    migrateScene(scene) {
        return scene.withMutations(map => {
            // migrate scene children
            map.get('children', []).forEach((_, c) => {
                const path = ['children', c];
                migrateAsset(map, path);
            });

            // migrate scene subslides
            map.get('subslides', []).forEach((subslide, s) => {
                subslide.get('children', []).forEach((child, c) => {
                    if (child.get('classType') !== 'TimeAwareMediaAsset') {
                        return;
                    }
                    const path = ['subslides', s, 'children', c];
                    migrateAsset(map, path);
                });
            });
        });
    },
});


function migrateAsset(map, path) {
    const asset = map.getIn(path);
    if (!asset) {
        return;
    }
    if (asset.get('classType') !== 'TimeAwareMediaAsset') {
        return;
    }
    const filters = asset.get('filters');
    if (!filters) {
        return;
    }

    const greyscaleFilters = filters.filter(filter => filter.get('classType') === 'GreyscaleFilter');
    if (greyscaleFilters.size === 0) {
        return;
    }

    // we currently don't have scene designs with multiple greyscale filters, so we can just take the first one
    const greyscaleFilter = greyscaleFilters.first();

    const adjustmentFilters = filters.filter(filter => filter.get('classType') === 'AdjustmentFilter');
    if (adjustmentFilters.size > 0) {
        // we currently don't use adjustment & greyscale filters together, but just in case we won't adjust
        // brightness for these cases
        return;
    }

    // add a brightness filter to compensate for the new greyscale filter brightness
    const adjustmentFilter = Immutable.fromJS({
        classType: 'AdjustmentFilter',
        name: 'AdjustmentFilter_1',
        brightness: mapOldGreyscaleToBrightness(greyscaleFilter.get('greyscale')),
        contrast: 1,
    });
    map.setIn([...path, 'filters'], filters.push(adjustmentFilter));
}

function mapOldGreyscaleToBrightness(greyscale: number) {
    // This was validated with our visual diff tool and matching a brightness value with the new
    // greyscale filter to have a similar appearance as the old greyscale filter. So for example these seemed equivalent:
    // - old greyscale 0.3 and brightness 0.87
    // - old greyscale 0.4 and brightness 1.3
    // - old greyscale 0.5 and brightness 1.5
    // If you look at the src code of the shaders, these numbers actually make sense.
    // You can see the color matrix code here: https://github.com/pixijs/pixijs/blob/v7.4.2/packages/filter-color-matrix/src/ColorMatrixFilter.ts#L162
    // and the new grayscale here: https://github.com/pixijs/filters/blob/v5.3.0/filters/grayscale/src/grayscale.frag#L6
    // In the grayscale filter we multiply the pixels on average by 0.33, while the matrix multiplies everything by the scale.
    // So if we take a color matrix greyscale value of 0.5 previously, then 0.5/0.33 = 1.5 to keep a similar value.
    return Math.round((greyscale * 3) * 100) / 100;
}
